// Market finding categories on the frontend: the same four values the Sonar
// prompt tags with (lambda/shared/market-categories.ts; keep the two lists
// identical), plus the PM-facing labels and the grouping the market view
// renders. Pure: no React, no I/O.

import type { NodeMetadata } from '@/services/canvas-mappers';

export const MARKET_CATEGORIES = ['demand', 'competitor', 'gap', 'trend'] as const;
export type MarketCategory = (typeof MARKET_CATEGORIES)[number];

// Exact-match guard for stored metadata. Anything else is undefined, which the
// grouping and the label both treat as "untagged".
export function parseMarketCategory(value: unknown): MarketCategory | undefined {
  return typeof value === 'string' && (MARKET_CATEGORIES as readonly string[]).includes(value)
    ? (value as MarketCategory)
    : undefined;
}

export type SectionKey = MarketCategory | 'uncategorised';

export interface CategorySection {
  key: SectionKey;
  // Section header in the market view (plural where that reads naturally).
  heading: string;
  // Singular, for pickers and the empty-state line.
  name: string;
  // One line under the heading: what this kind of finding tells you.
  blurb: string;
}

export const CATEGORY_SECTIONS: readonly CategorySection[] = [
  { key: 'demand', heading: 'Demand', name: 'Demand', blurb: 'Evidence people have this problem and pay to solve it' },
  { key: 'competitor', heading: 'Competitors', name: 'Competitor', blurb: 'Who already serves this segment, and how' },
  { key: 'gap', heading: 'Gaps', name: 'Gap', blurb: "Needs existing options don't serve" },
  { key: 'trend', heading: 'Trends', name: 'Trend', blurb: 'Shifts that change whether now is the right time' },
];

export const UNCATEGORISED_SECTION: CategorySection = {
  key: 'uncategorised',
  heading: 'Uncategorised',
  name: 'Uncategorised',
  blurb: 'Findings without a category',
};

const FINDING_TYPE_LABELS: Record<MarketCategory, string> = {
  demand: 'Demand finding',
  competitor: 'Competitor finding',
  gap: 'Gap finding',
  trend: 'Trend finding',
};

export const DEFAULT_FINDING_LABEL = 'Market finding';

// The type eyebrow for a market_signal node: category-specific when tagged,
// the generic label otherwise. Shared by the canvas card, the node modal header
// and the step-7 re-ground card so the three always agree.
export function findingTypeLabel(metadata: NodeMetadata | undefined): string {
  const category = parseMarketCategory(metadata?.category);
  return category ? FINDING_TYPE_LABELS[category] : DEFAULT_FINDING_LABEL;
}

export interface FindingSection<T> {
  section: CategorySection;
  items: T[];
}

// Group findings for the market view. Always returns the four canonical
// sections in fixed order, empty ones included, so the view can show a quiet
// "none yet" line where research came back thin. The uncategorised section is
// appended only when something landed there. Items keep their input order.
export function groupFindingsByCategory<T>(
  items: readonly T[],
  categoryOf: (item: T) => unknown,
): FindingSection<T>[] {
  const buckets: Record<SectionKey, T[]> = { demand: [], competitor: [], gap: [], trend: [], uncategorised: [] };
  for (const item of items) {
    buckets[parseMarketCategory(categoryOf(item)) ?? 'uncategorised'].push(item);
  }
  const out: FindingSection<T>[] = CATEGORY_SECTIONS.map((section) => ({ section, items: buckets[section.key] }));
  if (buckets.uncategorised.length > 0) out.push({ section: UNCATEGORISED_SECTION, items: buckets.uncategorised });
  return out;
}
